import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Activity, AlertTriangle, Bell, BookOpen, Building2, CheckCircle2, Circle, Clock, Gauge, Rocket, RefreshCw, ScanSearch } from 'lucide-react'
import { Badge, EmptyState, PageHeader, SegmentedControl, StatCard, formatDuration, formatRelative, type BadgeTone } from '@/kit/ui'
import { PERIODS, loadRunCounters, startSteps, storeCounters, type DashboardRuns, type RunEnv } from '@/lib/dashboard'
import { useHubStore } from '@/store/hubStore'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import { ANSWER_KINDS, type AnswerKind, type LenderStatus } from '@/types'

const ANSWER_COLOUR: Record<AnswerKind, string> = { offer: 'bg-emerald-500', approved: 'bg-emerald-700', redirect: 'bg-sky-500', pending: 'bg-amber-400', rejected: 'bg-slate-400', duplicate: 'bg-violet-400', error: 'bg-red-500' }
const STATUS_TONE: Record<LenderStatus, BadgeTone> = { active: 'success', draft: 'gray', off: 'danger' }
const pct = (part: number, all: number) => (all ? Math.round((part / all) * 1000) / 10 : 0)

/** The first screen (C-1, instead of the screen map): the Hub at a glance. Counts only, no applicant data. */
export function DashboardScreen() {
  const navigate = useNavigate()
  const lenders = useHubStore((s) => s.lenders)
  const alerts = useHubStore((s) => s.alerts)
  const history = useHubStore((s) => s.history)
  const batches = useCodeStore((s) => s.batches)
  const captured = useCodeStore((s) => s.captured)
  const flows = useFlowStore((s) => s.flows)
  const endpoints = useFlowStore((s) => s.endpoints)
  const [env, setEnv] = useState<RunEnv>('production')
  const [days, setDays] = useState('7')
  const [runs, setRuns] = useState<DashboardRuns | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let live = true
    setError(null)
    loadRunCounters(Number(days), env).then((r) => live && setRuns(r)).catch((err: Error) => live && setError(err.message))
    return () => { live = false }
  }, [days, env, tick])

  const c = useMemo(() => storeCounters({ lenders, endpoints, alerts, captured }), [lenders, endpoints, alerts, captured])
  const steps = useMemo(() => startSteps({ lenders, batches, flows, endpoints }), [lenders, batches, flows, endpoints])
  const name = (slug: string) => lenders.find((l) => l.slug === slug)?.name ?? slug
  const period = PERIODS.find((p) => p.value === days)?.label.toLowerCase() ?? `${days} days`
  const errors = runs?.byAnswer.error ?? 0
  const errorRate = runs ? pct(errors, runs.total) : 0
  const allDone = steps.every((s) => s.done)
  const openAlerts = alerts.filter((a) => !a.acknowledgedAt).slice(0, 5)

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="The Hub at a glance: lenders, endpoints, runs and alerts. Counts only; the Hub keeps no applicant data."
        actions={
          <>
            <SegmentedControl size="sm" ariaLabel="Runs from" value={env} onChange={setEnv} options={[{ value: 'production', label: 'Production' }, { value: 'uat', label: 'UAT' }]} />
            <select className="input w-auto" aria-label="Period" value={days} onChange={(e) => setDays(e.target.value)}>
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setTick((n) => n + 1)} aria-label="Refresh"><RefreshCw size={13} /> Refresh</button>
          </>
        }
      />

      {!allDone && (
        <section className="card mb-4" data-testid="getting-started">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="section-title !mb-0">Getting started: a lender from UAT to production</h2>
            <a href="/docs/quick-start" target="_blank" rel="noopener" className="btn btn-secondary btn-sm" title="Opens in a new tab"><BookOpen size={13} /> Developer docs</a>
          </div>
          <ol className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {steps.map((s, i) => (
              <li key={s.title}>
                <Link to={s.to} className={`flex h-full gap-2 rounded border px-3 py-2 no-underline transition hover:border-primary ${s.done ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30' : 'border-slate-200 dark:border-slate-700'}`} data-testid={`start-step-${i + 1}`} data-done={s.done}>
                  {s.done ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" aria-label="done" /> : <Circle size={16} className="mt-0.5 shrink-0 text-muted" aria-label="to do" />}
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-default">{i + 1}. {s.title}</span>
                    <span className="block text-2xs text-muted">{s.detail}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="kpis">
        <StatCard icon={Building2} label="Lenders active" value={c.lenders.active} hint={`${c.lenders.total} in all · ${c.lenders.draft} draft · ${c.lenders.off} off`} onClick={() => navigate('/lenders')} />
        <StatCard icon={Activity} label="Endpoints live" value={c.endpoints.live} hint={`${c.endpoints.test} UAT only · ${c.endpoints.stopped} stopped`} onClick={() => navigate('/endpoints')} />
        <StatCard icon={Rocket} label="Lender APIs live" value={c.liveApis} hint="lenders with a built batch live in production" tone="accent" />
        <StatCard icon={Bell} label="Open alerts" value={c.openAlerts} hint={c.newCodes ? `${c.newCodes} new lender code(s) to look at` : 'no new lender codes'} tone={c.openAlerts ? 'danger' : 'gray'} onClick={() => navigate('/alerts')} />
        <StatCard icon={ScanSearch} label={`Runs · ${period}`} value={runs ? runs.total : '…'} hint={env === 'production' ? 'production: the web app and the website' : 'UAT: test calls and the playground'} onClick={() => navigate('/runs')} />
        <StatCard icon={Gauge} label="Success rate" value={runs?.total ? `${(100 - errorRate).toFixed(1)}%` : '—'} hint={runs?.total ? `${errors} error(s) of ${runs.total}` : 'no runs in this period'} tone={errorRate > 5 ? 'danger' : 'primary'} />
        <StatCard icon={Clock} label="Average answer time" value={runs ? formatDuration(runs.avgMs) : '…'} hint={runs?.p95Ms !== null && runs?.p95Ms !== undefined ? `95 of 100 within ${formatDuration(runs.p95Ms)}` : 'no runs in this period'} />
        <StatCard icon={AlertTriangle} label="Errors" value={runs ? errors : '…'} hint={runs?.total ? `${errorRate}% of the runs` : 'none'} tone={errors ? 'danger' : 'gray'} />
      </div>
      {error && <p className="mb-3 text-xs text-red-600" role="alert">The run counters did not load: {error}</p>}

      <div className="mb-4 grid grid-cols-1 gap-3 xl:grid-cols-3">
        <section className="card xl:col-span-2" aria-label="Runs per day">
          <h2 className="section-title">Runs per day · {period} · {env === 'production' ? 'production' : 'UAT'}</h2>
          {!runs ? <p className="py-6 text-center text-xs text-muted">Loading…</p> : runs.total > 0 ? <RunsChart runs={runs} /> : <p className="py-6 text-center text-xs text-muted">No runs in this period yet.</p>}
        </section>
        <section className="card" aria-label="Answers">
          <h2 className="section-title">Answers</h2>
          {runs && runs.total > 0 ? (
            <>
              <div className="mb-3 flex h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                {ANSWER_KINDS.filter((a) => runs.byAnswer[a]).map((a) => <span key={a} className={ANSWER_COLOUR[a]} style={{ width: `${pct(runs.byAnswer[a], runs.total)}%` }} title={`${a}: ${runs.byAnswer[a]}`} />)}
              </div>
              <ul className="space-y-1 text-xs">
                {ANSWER_KINDS.map((a) => (
                  <li key={a} className="flex items-center gap-2" data-testid={`answer-${a}`}>
                    <span className={`h-2.5 w-2.5 rounded-sm ${ANSWER_COLOUR[a]}`} />
                    <span className="flex-1">{a}</span>
                    <span className="tabular-nums">{runs.byAnswer[a]}</span>
                    <span className="w-12 text-right tabular-nums text-muted">{pct(runs.byAnswer[a], runs.total)}%</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <p className="py-6 text-center text-xs text-muted">Offers, rejections and errors show here once runs come in.</p>}
        </section>
      </div>

      <section className="card-flush mb-4 overflow-x-auto" aria-label="Lenders">
        <h2 className="section-title px-4 pt-3">Lenders</h2>
        {lenders.length === 0 ? (
          <EmptyState icon={Building2} title="No lenders yet" description="Add the first lender, then follow Getting started above." actionLabel="+ Add a lender" onAction={() => navigate('/lenders/new')} compact />
        ) : (
          <table className="table">
            <thead><tr><th>Lender</th><th>Status</th><th>Live API</th><th className="text-right">Endpoints live / UAT</th><th className="text-right">Runs · {period}</th><th className="text-right">Errors</th><th className="text-right">Average time</th><th className="text-right">Open alerts</th></tr></thead>
            <tbody>
              {lenders.map((l) => {
                const mine = endpoints.filter((e) => e.lenderSlug === l.slug && e.state !== 'retired')
                const r = runs?.perLender.find((x) => x.lenderSlug === l.slug)
                const open = alerts.filter((a) => a.lenderSlug === l.slug && !a.acknowledgedAt).length
                return (
                  <tr key={l.slug} data-testid={`dash-lender-${l.slug}`}>
                    <td><Link to={`/lenders/${l.slug}/info`}>{l.name}</Link></td>
                    <td><Badge tone={STATUS_TONE[l.status]}>{l.status}</Badge></td>
                    <td className="mono">{l.liveBatch ?? '—'}</td>
                    <td className="text-right tabular-nums">{mine.filter((e) => e.env === 'uat_prod').length} / {mine.filter((e) => e.env === 'uat').length}</td>
                    <td className="text-right tabular-nums">{r?.total ?? 0}</td>
                    <td className={`text-right tabular-nums ${r?.errors ? 'text-red-600' : ''}`}>{r ? `${r.errors} (${pct(r.errors, r.total)}%)` : '—'}</td>
                    <td className="text-right tabular-nums">{formatDuration(r?.avgMs)}</td>
                    <td className="text-right tabular-nums">{open || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="card" aria-label="Recent changes">
          <h2 className="section-title">Recent changes</h2>
          {history.length === 0 ? <p className="text-xs text-muted">Nothing changed yet.</p> : (
            <ul className="space-y-1.5 text-xs">
              {history.slice(0, 8).map((h) => (
                <li key={h.id} className="flex gap-2">
                  <span className="w-20 shrink-0 text-muted">{formatRelative(h.at)}</span>
                  <span className="min-w-0"><b>{h.lenderSlug ? name(h.lenderSlug) : 'Hub'}</b> · {h.where}: {h.what} <span className="text-muted">({h.who})</span></span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="card" aria-label="Open alerts">
          <h2 className="section-title">Open alerts</h2>
          {openAlerts.length === 0 ? <p className="text-xs text-muted">No open alert.</p> : (
            <ul className="space-y-1.5 text-xs">
              {openAlerts.map((a) => (
                <li key={a.id} className="flex gap-2">
                  <Badge tone={a.severity === 'danger' ? 'danger' : a.severity === 'warning' ? 'warning' : 'info'}>{a.severity}</Badge>
                  <span className="min-w-0 flex-1">{a.lenderSlug ? <b>{name(a.lenderSlug)}: </b> : null}{a.text}</span>
                  <span className="shrink-0 text-muted">{formatRelative(a.at)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/alerts" className="mt-2 inline-block text-xs">All alerts →</Link>
        </section>
      </div>
    </>
  )
}

/** Bars per day: the whole bar = runs, its red part = errors. */
function RunsChart({ runs }: { runs: DashboardRuns }) {
  const top = Math.max(1, ...runs.perDay.map((d) => d.total))
  const label = (day: string) => new Date(`${day}T12:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  const every = runs.perDay.length > 10 ? 5 : 1
  return (
    <div data-testid="runs-chart">
      <div className="flex h-40 items-end gap-1">
        {runs.perDay.map((d) => (
          <div key={d.day} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${label(d.day)}: ${d.total} run(s), ${d.errors} error(s)`}>
            <div className="flex flex-col justify-end overflow-hidden rounded-t bg-primary/80" style={{ height: `${(d.total / top) * 100}%`, minHeight: d.total ? 2 : 0 }}>
              {d.errors > 0 && <div className="bg-red-500" style={{ height: `${(d.errors / d.total) * 100}%` }} />}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1 text-[10px] text-muted">
        {runs.perDay.map((d, i) => <span key={d.day} className="min-w-0 flex-1 truncate text-center">{i % every === 0 || i === runs.perDay.length - 1 ? label(d.day) : ''}</span>)}
      </div>
      <p className="mt-1 text-2xs text-muted"><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-primary/80" />runs <span className="ml-3 mr-1 inline-block h-2 w-2 rounded-sm bg-red-500" />errors · days in India time</p>
    </div>
  )
}
